import React, { useState, useEffect } from "react";
import { Modal, Form, Input, InputNumber, Select, Radio, message, Row, Col, Alert } from "antd";
import { useCreate, useUpdate } from "../../../../hooks/useCrud";
import { masterDataService, flightLegService } from "../../../../api/enhancedApi";
import { STC_TYPE_OPTIONS, LEG_TYPE_OPTIONS, DEFAULT_STC_TYPE, DEFAULT_LEG_TYPE } from "../../../../constants/flightConstants";

const { Option } = Select;
const { TextArea } = Input;

/**
 * 航段表单组件
 * @param {Object} props
 * @param {boolean} props.visible - 对话框是否可见
 * @param {string} props.mode - 模式，'add'或'edit'
 * @param {Object} props.initialValues - 初始值
 * @param {Function} props.onCancel - 取消回调
 * @param {Function} props.onSuccess - 成功回调
 */
const FlightLegForm = ({ visible, mode, initialValues, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const isEdit = mode === "edit";
    const [airportOptions, setAirportOptions] = useState([]);
    const [legExistsWarning, setLegExistsWarning] = useState(false);

    // 创建和更新航段的Hook
    const createMutation = useCreate("flightLegs", flightLegService.createFlightLeg, {
        onSuccess: () => {
            message.success("添加航段成功");
            onSuccess();
        },
        onError: (error) => {
            // 409错误表示航段已存在，提供更友好的提示
            if (error.response?.status === 409) {
                const depAirport = form.getFieldValue("departure_airport");
                const arrAirport = form.getFieldValue("arrival_airport");
                message.error(`航段 ${depAirport}-${arrAirport} 已存在，请选择不同的机场组合`);
                return; // 已处理，不再继续
            }
            
            // 【航段航路BH优化】400错误表示校验失败（如航路不存在）
            if (error.response?.status === 400) {
                const errorDetail = error.response?.data?.detail || "创建航段失败";
                message.error(errorDetail, 6); // 显示6秒，让用户看清楚
                return; // 已处理，不再继续
            }
            
            // 其他错误显示通用提示
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || "创建航段失败，请稍后重试";
            message.error(errorMsg);
        },
    });

    const updateMutation = useUpdate("flightLegs", flightLegService.updateFlightLeg, {
        onSuccess: () => {
            message.success("更新航段成功");
            onSuccess();
        },
        onError: (error) => {
            // 【航段航路BH优化】400错误表示校验失败（如航路不存在）
            if (error.response?.status === 400) {
                const errorDetail = error.response?.data?.detail || "更新航段失败";
                message.error(errorDetail, 6); // 显示6秒，让用户看清楚
                return;
            }
            
            // 其他错误显示通用提示
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || "更新航段失败，请稍后重试";
            message.error(errorMsg);
        },
    });

    // 加载机场数据
    useEffect(() => {
        // 2. 加载真实机场数据
        const loadAirports = async () => {
            try {
                // 使用 masterDataService 获取所有机场的约束信息 (包含基础信息)
                // 注意: 这个接口返回的是一个数组，而不是分页结构
                const response = await masterDataService.airport.getAllAirportConstraints({ is_active: true }); // 只获取激活的机场

                // 检查响应是否是数组
                if (response && response.data && Array.isArray(response.data)) {
                    const formattedOptions = response.data.map((airport) => ({
                        code: airport.iata_code,
                        name: `${airport.iata_code}${airport.icao_code ? ` - ${airport.icao_code}` : ""}${airport.airport_name ? ` (${airport.airport_name})` : ""}`,
                    }));
                    setAirportOptions(formattedOptions);
                } else {
                    console.error("加载机场数据失败或格式不正确 (期望数组):", response);
                    message.error("加载机场选项失败");
                    setAirportOptions([]); // 加载失败则清空
                }
            } catch (error) {
                console.error("加载机场数据异常:", error);
                message.error("加载机场选项异常");
                setAirportOptions([]); // 加载异常则清空
            }
        };

        loadAirports(); // 调用加载函数
    }, []); // 空依赖数组，仅在组件挂载时执行一次

    // 分钟转换为小时和分钟
    const minutesToHourMinute = (totalMinutes) => {
        if (!totalMinutes && totalMinutes !== 0) return { hours: "", minutes: "" };
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return { hours, minutes };
    };

    // 小时和分钟转换为总分钟数
    const hourMinuteToMinutes = (hours, minutes) => {
        return (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    };

    // 设置表单初始值
    useEffect(() => {
        if (initialValues) {
            // 调试：打印初始值以检查数据结构
            console.log("FlightLegForm initialValues:", initialValues);

            // 【航段航路BH优化】不再设置block_hour相关字段，BH将从航路自动获取
            form.setFieldsValue({
                departure_airport: initialValues.departure_airport,
                arrival_airport: initialValues.arrival_airport,
                leg_number: initialValues.leg_number || "",
                distance_km: initialValues.distance_km,
                leg_type: initialValues.leg_type || DEFAULT_LEG_TYPE,
                stc_type: initialValues.stc_type || DEFAULT_STC_TYPE,
                is_active: initialValues.is_active !== undefined ? initialValues.is_active : true,
                remarks: initialValues.remarks || "",
            });
        }
    }, [initialValues, form]);

    // 提交表单前处理时间字段
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // 【航段航路BH优化】不再提交block_hour_minutes字段，后端将从航路自动获取
            // 构建提交数据
            const submitData = {
                departure_airport: values.departure_airport,
                arrival_airport: values.arrival_airport,
                leg_number: values.leg_number,
                distance_km: values.distance_km,
                // block_hour_minutes 字段已移除，将由后端从航路自动获取
                leg_type: values.leg_type,
                is_active: values.is_active,
                stc_type: values.stc_type,
                remarks: values.remarks,
            };

            if (isEdit) {
                // 检查必要的ID字段
                if (!initialValues?.departure_airport || !initialValues?.arrival_airport) {
                    console.error("编辑模式下缺少必要的ID字段:", {
                        departure_airport: initialValues?.departure_airport,
                        arrival_airport: initialValues?.arrival_airport,
                        initialValues,
                    });
                    message.error("编辑失败：缺少必要的航段标识信息");
                    return;
                }

                // 更新航段
                updateMutation.mutate({
                    id: {
                        departure_airport: initialValues.departure_airport,
                        arrival_airport: initialValues.arrival_airport,
                    },
                    data: submitData,
                });
            } else {
                // 创建航段前检查是否已存在
                if (legExistsWarning) {
                    message.error("该航段已存在，无法重复创建");
                    return;
                }

                // 创建航段
                createMutation.mutate(submitData);
            }
        } catch (error) {
            console.error("表单验证失败:", error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 检查航段是否已存在
    const checkLegExists = async (depAirport, arrAirport) => {
        if (!depAirport || !arrAirport || isEdit) {
            setLegExistsWarning(false);
            return;
        }

        try {
            // 【航段航路BH优化】传入silentOn404=true，让404不显示错误日志
            // 因为在检查重复时，404表示航段不存在（可以创建），是正常情况
            await flightLegService.getFlightLegByRoute(depAirport, arrAirport, true);
            // 如果没有抛出错误，说明航段存在
            setLegExistsWarning(true);
        } catch (error) {
            // 404错误表示航段不存在，这是我们期望的
            if (error.response?.status === 404) {
                setLegExistsWarning(false);
            } else {
                console.error("检查航段是否存在时出错:", error);
                setLegExistsWarning(false);
            }
        }
    };

    // 到达机场选项：允许与出发机场相同（支持飞本场），不再过滤出发机场
    const getArrivalAirportOptions = () => {
        return airportOptions;
    };

    return (
        <Modal title={isEdit ? "编辑航段" : "添加航段"} open={visible} onOk={handleSubmit} onCancel={handleCancel} confirmLoading={createMutation.isLoading || updateMutation.isLoading} maskClosable={false} width={720}>
            <Form form={form} layout="horizontal" labelAlign="left">
                <div className="form-inline-group">
                    <Form.Item name="departure_airport" label="出发机场" rules={[{ required: true, message: "请选择出发机场" }]}>
                        <Select
                            placeholder="请选择出发机场"
                            showSearch
                            optionFilterProp="children"
                            disabled={isEdit} // 编辑模式不允许修改出发机场
                            onChange={() => {
                                const depAirport = form.getFieldValue("departure_airport");
                                const arrAirport = form.getFieldValue("arrival_airport");
                                // 允许起降相同（飞本场）：两端都已选即检查航段是否已存在
                                if (depAirport && arrAirport) {
                                    checkLegExists(depAirport, arrAirport);
                                } else {
                                    setLegExistsWarning(false);
                                }
                            }}
                        >
                            {airportOptions.map((airport) => (
                                <Option key={airport.code} value={airport.code}>
                                    {airport.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="arrival_airport"
                        label="到达机场"
                        rules={[{ required: true, message: "请选择到达机场" }]}
                    >
                        <Select
                            placeholder="请选择到达机场"
                            showSearch
                            optionFilterProp="children"
                            disabled={isEdit} // 编辑模式不允许修改到达机场
                            onChange={() => {
                                const depAirport = form.getFieldValue("departure_airport");
                                const arrAirport = form.getFieldValue("arrival_airport");
                                // 允许起降相同（飞本场）：两端都已选即检查航段是否已存在
                                if (depAirport && arrAirport) {
                                    checkLegExists(depAirport, arrAirport);
                                } else {
                                    setLegExistsWarning(false);
                                }
                            }}
                        >
                            {getArrivalAirportOptions().map((airport) => (
                                <Option key={airport.code} value={airport.code}>
                                    {airport.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="leg_number" label="航段编号" rules={[{ pattern: /^\d+$/, message: "航段编号必须为数字" }]} tooltip="航段编号为可选字段，如100、230、300等">
                        <Input placeholder="请输入航段编号，如100、230、300等（可选）" />
                    </Form.Item>

                    <Form.Item name="stc_type" label="航班性质" rules={[{ required: true, message: "请选择航班性质" }]}>
                        <Select placeholder="请选择航班性质">
                            {STC_TYPE_OPTIONS.map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="leg_type" label="航段类型" rules={[{ required: true, message: "请选择航段类型" }]}>
                        <Select placeholder="请选择航段类型">
                            {LEG_TYPE_OPTIONS.map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {/* 【航段航路BH优化】隐藏标准轮挡时间字段，BH将从航路自动获取 */}
                    {/* <Form.Item label="标准轮挡时间" required className="time-input-group">
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    name="block_hour_hours"
                                    noStyle
                                    rules={[
                                        { required: true, message: "请输入小时数" },
                                        { type: "number", min: 0, message: "小时数必须大于等于0" },
                                    ]}
                                >
                                    <InputNumber placeholder="小时" min={0} precision={0} style={{ width: "100%" }} addonAfter="时" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="block_hour_minutes"
                                    noStyle
                                    rules={[
                                        { required: true, message: "请输入分钟数" },
                                        { type: "number", min: 0, max: 59, message: "分钟数必须在0-59之间" },
                                    ]}
                                >
                                    <InputNumber placeholder="分钟" min={0} max={59} precision={0} style={{ width: "100%" }} addonAfter="分" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form.Item> */}
                </div>

                {/* 航段已存在警告 */}
                {legExistsWarning && !isEdit && (
                    <Alert
                        message="警告"
                        description={`航段 ${form.getFieldValue("departure_airport")}-${form.getFieldValue("arrival_airport")} 已存在，无法重复创建。`}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form.Item name="is_active" label="状态">
                    <Radio.Group>
                        <Radio value={true}>启用</Radio>
                        <Radio value={false}>停用</Radio>
                    </Radio.Group>
                </Form.Item>

                <Form.Item name="remarks" label="备注">
                    <TextArea rows={3} placeholder="请输入备注信息(可选)" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default FlightLegForm;
